import type {
  ExternalDropdownOption,
  InternalDropdownOption,
} from '~/components/DropdownTreeSingleSelect/types';
import { updateIsVisibleTrueRecursive } from '~/components/DropdownTreeSingleSelect/utils/updateIsVisibleTrueRecursive';
import { updateSelectedRecursive } from '~/components/DropdownTreeSingleSelect/utils/updateSelectedRecursive';

export interface GetExternalToInternalOptionsResult {
  internalOptions: InternalDropdownOption[];
  selectedOption?: InternalDropdownOption;
}

/**
 * Transforms external dropdown options into internal dropdown options.
 * @param externalOptions - The external dropdown options to transform.
 * @param param1.optionIndexPath - The current option index path.
 * @param param1.selectedValue - The currently selected value.
 * @returns The transformed internal dropdown options.
 */
export function updateExternalToInternalRecursive(
  externalOptions: ExternalDropdownOption[],
  {
    optionIndexPath,
    selectedValue,
  }: { optionIndexPath: number[]; selectedValue?: string },
): GetExternalToInternalOptionsResult {
  let selectedOption: InternalDropdownOption | undefined;

  const internalOptions = externalOptions.map((option, optionIndex) => {
    const isSelected = option.value === selectedValue;
    const { children, ...restOption } = option;

    const internalOption: InternalDropdownOption = {
      ...restOption,
      // Augment external option with default internal values.
      isSelected,
      hasSelected: false,
      isVisible: true,
      isExpanded: false,
      optionIndexPath: [...optionIndexPath, optionIndex],
    };

    if (isSelected) {
      selectedOption = internalOption;
    }

    if (children) {
      const {
        internalOptions: childInternalOptions,
        selectedOption: childSelectedOption,
      } = updateExternalToInternalRecursive(children, {
        optionIndexPath: internalOption.optionIndexPath,
        selectedValue,
      });

      internalOption.children = childInternalOptions;

      if (childSelectedOption) {
        selectedOption = childSelectedOption;
      }
    }

    return internalOption;
  });

  if (!selectedOption || optionIndexPath.length > 0) {
    return { internalOptions, selectedOption };
  }

  const optionsWithSelection = updateSelectedRecursive(internalOptions, {
    isSelected: true,
    indexPath: selectedOption.optionIndexPath,
  });

  return {
    internalOptions: updateIsVisibleTrueRecursive(optionsWithSelection),
    selectedOption,
  };
}
